/**
 * The contact-photo storage CONTRACT, in one place (pure, client-safe).
 *
 * `contacts.avatar_url` holds a Hub-relative path to the authenticated serving
 * route — never a third-party URL:
 *
 *     /api/contacts/<contactId>/avatar?v=<sha256 of the stored bytes>
 *
 * WHY a Hub path and not Google's `lh3.googleusercontent.com` URL: a provider
 * URL can change or expire under us, and rendering it makes every viewer's
 * browser call Google (and tells Google who is looking at which contact). The
 * bytes are copied into `wtc-hub-blobs` once; Hub serves them behind its own
 * session (HUB_AUTHZ_LAYERS — never a public bucket).
 *
 * WHY the `v=` hash is in the stored value: the object key is derived from it
 * (`private/contacts/<id>/avatar-<sha256>.webp`), so a new photo is a new URL —
 * the browser can cache a given URL forever, and a replaced photo can never be
 * shadowed by a cached old one. The serving route reads the hash from the ROW,
 * not from the request (a caller-supplied `v` only decides cache headers).
 *
 * Every stored avatar is re-encoded to WebP server-side, so the extension is
 * fixed and the key is fully derivable from `(contactId, sha256)`.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHA256_HEX = /^[0-9a-f]{64}$/;
const AVATAR_PATH = /^\/api\/contacts\/([0-9a-fA-F-]{36})\/avatar\?v=([0-9a-f]{64})$/;

/** True for a canonical contact UUID (the route's path parameter). */
export function isContactId(value: string): boolean {
  return UUID.test(value);
}

/** The value written to `contacts.avatar_url` for a stored photo. */
export function contactAvatarPath(contactId: string, sha256: string): string {
  if (!isContactId(contactId)) throw new Error("contactAvatarPath: contactId must be a UUID");
  if (!SHA256_HEX.test(sha256)) throw new Error("contactAvatarPath: sha256 must be 64 lowercase hex chars");
  return `/api/contacts/${contactId.toLowerCase()}/avatar?v=${sha256}`;
}

/**
 * The stored photo's hash when `value` is THIS contact's Hub avatar path, else
 * null (empty, a legacy external URL, or another contact's path).
 */
export function parseContactAvatarPath(value: string | null | undefined, contactId: string): string | null {
  if (!value) return null;
  const match = AVATAR_PATH.exec(value.trim());
  if (!match) return null;
  if (match[1].toLowerCase() !== contactId.toLowerCase()) return null;
  return match[2];
}

/** True when `value` is any contact's Hub avatar path (same-origin, safe to render). */
export function isHubContactAvatarPath(value: string | null | undefined): boolean {
  return Boolean(value && AVATAR_PATH.test(value.trim()));
}

/** Object-key segments under `private/` for a stored photo (see the module header). */
export function contactAvatarKeySegments(contactId: string, sha256: string): string[] {
  if (!isContactId(contactId) || !SHA256_HEX.test(sha256)) {
    throw new Error("contactAvatarKeySegments: invalid contactId or sha256");
  }
  return ["contacts", contactId.toLowerCase(), `avatar-${sha256}.webp`];
}

/**
 * `src` for an `<img>` of a contact's photo, or null when there is nothing Hub
 * can render safely. A Hub avatar path renders as-is. A legacy `https:` value
 * (written by older enrichment / webhook paths before this contract) still
 * renders, because hiding a photo someone set would be a regression; anything
 * else (javascript:, data:, http:, garbage) is refused and the initials show.
 */
export function renderableAvatarSrc(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (isHubContactAvatarPath(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}
